// base-chart.component.ts
import {
  Component,
  Input,
  OnInit,
  ElementRef,
  OnDestroy,
  OnChanges,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import HC_stock from 'highcharts/modules/stock';
HC_stock(Highcharts);
import { ChartConfigService } from '../services/chart-config.service';

@Component({
  selector: 'app-base-chart',
  template: '<div class="chart-container"></div>',
})
export abstract class BaseHighChartComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() chartData: Highcharts.SeriesOptionsType[] = [];
  @Input() colorPalette: string[] = [];
  @Input() height: number | string = 'auto';
  @Input() useStockChart: boolean = true;

  protected chart: Highcharts.Chart | undefined;
  private chartId: string;
  private resizeObserver?: ResizeObserver;

  constructor(
    protected elementRef: ElementRef,
    protected configService: ChartConfigService,
  ) {
    this.chartId = `chart-${Math.random().toString(36).substring(7)}`;
  }

  ngOnInit() {
    this.createChart();
  }

  ngOnChanges() {
    if (this.chart) {
      this.chart.update(this.getChartConfig(), true);
    } else if (this.chartData?.length > 0) {
      this.createChart();
    }
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  /**
   * Triggers a reflow of the chart to recalculate dimensions.
   * Call this after container size changes (e.g., window resize, sidebar toggle).
   */
  public reflow(): void {
    this.chart?.reflow();
  }

  /**
   * Returns whether the chart instance exists and is ready
   */
  public isChartReady(): boolean {
    return !!this.chart;
  }

  protected abstract getChartConfig(): Highcharts.Options;

  protected createChart() {
    if (!this.chartData || this.chartData.length === 0) {
      console.warn('Chart data is not provided.');
      this.elementRef.nativeElement.querySelector(
        '.chart-container',
      ).innerHTML = 'No data to display';
      return;
    }

    const container: HTMLElement =
      this.elementRef.nativeElement.querySelector('.chart-container');
    if (!container) {
      return;
    }
    container.id = this.chartId;

    // Creating a chart while the container has zero width makes Highcharts
    // compute a negative plot width, producing the SVG error:
    // "<rect> attribute width: A negative value is not valid".
    // Defer until the container has a usable width (e.g. lazy-loaded modules
    // that mount before the flex/grid layout has settled).
    if (container.clientWidth <= 0) {
      this.deferUntilSized(container);
      return;
    }

    this.render(container);
  }

  private render(container: HTMLElement): void {
    const config = this.getChartConfig();
    this.chart = this.useStockChart
      ? Highcharts.stockChart(container, config)
      : Highcharts.chart(container, config);
    this.onChartCreated();
  }

  /**
   * Hook run after the chart is (re)created. No-op by default; subclasses override
   * it to wire chart-level behavior. The chart is on the protected `chart` field.
   */
  protected onChartCreated(): void {
    // intentionally empty
  }

  private deferUntilSized(container: HTMLElement): void {
    // Fallback for environments without ResizeObserver (e.g. jsdom in tests).
    if (typeof ResizeObserver === 'undefined') {
      this.render(container);
      return;
    }
    if (this.resizeObserver) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      if (container.clientWidth > 0) {
        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;
        if (!this.chart) {
          this.render(container);
        }
      }
    });
    this.resizeObserver.observe(container);
  }
}
